import { getErrorDetail } from "components/utility/utils";
import { fetchOrchidAPI } from "utils/api";
import { InvalidArgumentError } from "utils/error";
import { orchid_encrypt, orchidEncryptBinary } from "utils/OrchidEncryption";

import {
  orchidDriveFileFromAPI,
  OrchidDriveFile,
  orchidDriveUploadTaskFromAPI,
  OrchidDriveUploadTask,
} from "../types";

export async function createFileEntry(
  fileDataId: string,
  name: string,
  chunkSize: number,
  expectedChunkCount: number | null,
  parentDirectoryId: string | null,
  metaDataCipherKey: CryptoKey,
  patientUserId: string | null,
  uploadTaskId: string | null,
  orchidUniqueDeviceId: string | null,
  abortController?: AbortController,
): Promise<{
  fileEntry: OrchidDriveFile;
  uploadTaskEntry: OrchidDriveUploadTask;
}> {
  if (!name) throw new InvalidArgumentError("file name can't be empty");

  const response = await fetchOrchidAPI(
    `/api/drive_data/file_data/create_entry/${fileDataId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name_encrypted: await orchid_encrypt(name, metaDataCipherKey),
        patient_user_id: patientUserId,
        chunk_size: chunkSize,
        expected_chunk_count: expectedChunkCount,
        parent_directory_id: parentDirectoryId,
        shared_orchid_user_ids: [],
        upload_task_id: uploadTaskId,
        orchid_unique_device_id: orchidUniqueDeviceId,
      }),
      signal: abortController?.signal,
    },
  );
  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(getErrorDetail(responseText));
  }

  const responseData = await response.json();
  return {
    fileEntry: await orchidDriveFileFromAPI(
      responseData.file_data_entry,
      metaDataCipherKey,
    ),
    uploadTaskEntry: orchidDriveUploadTaskFromAPI(responseData.upload_task),
  };
}

export async function uploadChunk(
  fileDataId: string,
  chunkNum: number,
  chunk: ArrayBuffer,
  dataCipherKey: CryptoKey,
  metaDataCipherKey: CryptoKey,
  uploadComplete: boolean,
  abortController?: AbortController,
): Promise<OrchidDriveFile> {
  const response = await fetchOrchidAPI(
    `/api/drive_data/file_data/upload_chunk/${fileDataId}/${chunkNum}${
      uploadComplete ? "?upload_complete=true" : ""
    }`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: await orchidEncryptBinary(chunk, dataCipherKey),
      signal: abortController?.signal,
    },
  );
  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(getErrorDetail(responseText));
  }
  return await orchidDriveFileFromAPI(await response.json(), metaDataCipherKey);
}

export async function updateFileDigest(
  fileDataId: string,
  checksum_sha256: string,
): Promise<void> {
  const response = await fetchOrchidAPI(`/api/drive/checksum/${fileDataId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ checksum_sha256 }),
  });
  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(getErrorDetail(responseText));
  }
}
